/**
 * Creator v2 — the whole creation flow, one screen at a time.
 *
 * App owns the app shell, the paywall, publishing and the player; this component
 * owns the flow. Everything it needs from App arrives as props, so nothing here
 * imports App back.
 *
 * Progress is autosaved to the user's single draft as they move between screens.
 * It never silently replaces a draft that belongs to a different puzzle: if one
 * exists, autosave stays off and saving becomes an explicit choice on exit.
 *
 * The device Back button (and the swipe-back gesture) arrive through `backRef`:
 * App hands every system back press to the flow while it is mounted, so it steps
 * back one screen instead of leaving the creator and dropping what was written.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CreatorShell from "./chassis/CreatorShell";
import PreviewBar, { PREVIEW_BAR_HEIGHT } from "./chassis/PreviewBar";
import { isDarkPalette, paletteVars } from "./chassis/palettes";
import { PrimaryButton } from "./chassis/widgets";
import { fromDraftConfig, isDraftable, toDraftConfig, toPuzzleConfig } from "./config";
import ExitSheet from "./chassis/ExitSheet";
import { canContinue, completeClues, currentClue, initialState, isClueComplete, reducer, segmentOf } from "./state";
import { NameScreen, VibeScreen, startFooter } from "./screens/StartScreens";
import { AnswerScreen, CelebrationDetailScreen, CelebrationScreen, ClueNameScreen, HintScreen, QuestionScreen, RewardScreen, rewardFooter } from "./screens/ClueScreens";
import { ClueListScreen, listFooter } from "./screens/ClueListScreen";
import { ReadyScreen, RevealScreen, SealScreen, WordScreen, readyFooter } from "./screens/FinishScreens";
import LimitScreen from "./screens/LimitScreen";
import { generatePuzzleId, saveDraft, fetchDraft } from "../../services/puzzleService";
import { uploadPuzzleMedia } from "../../services/mediaService";
import { track } from "../../services/analytics";
import logger from "../../services/logger";

export default function CreatorFlow({
  initialConfig = null,
  initialName = "",
  initialTheme = null,
  themes,
  now,
  allSeasons = false,
  isPro = false,
  canCreate = true,
  slotLimit = 3,
  puzzles = [],
  onDeletePuzzle,
  onUpgrade,
  onLocked,
  onMediaLocked,
  onExit,
  onNewPuzzle,
  backRef,
  onSaveDraftExplicit,
  onPublished,
  onPrivacy,
  onInviteSignIn,
  onShared,
  ShareScreen,
  LevelPlay,
  Finale,
  themeVarsFor,
}) {
  const { t: tr } = useTranslation();

  const [state, dispatch] = useReducer(
    reducer,
    null,
    () =>
      initialConfig
        ? fromDraftConfig(initialConfig, { newPuzzleId: generatePuzzleId })
        : initialState({ puzzleId: generatePuzzleId(), recipientName: initialName || "", theme: initialTheme || null })
  );

  // At the limit we say so before anything is written — unless the user resumed a
  // draft or chose to build anyway.
  const [limitAcknowledged, setLimitAcknowledged] = useState(!!initialConfig);
  const [publishing, setPublishing] = useState(false);
  // "Play it through first" — the real player over the unsaved puzzle. Answers are
  // still plain text here (nothing is published yet), which is exactly the legacy
  // path LevelPlay already handles, so it plays for real.
  const [playthrough, setPlaythrough] = useState(null); // null | "play" | "finale"
  // Leaving while autosave is off (another draft exists) asks first.
  const [exitAsk, setExitAsk] = useState(false);
  // Autosave is disabled while someone else's draft is in the way (see below).
  const autosaveRef = useRef(true);
  const lastSavedRef = useRef("");

  const defaultTitle = tr("app.creator.ready.defaultTitle", { name: state.recipientName || tr("app.creator.ready.defaultName") });
  const puzzleConfig = useMemo(() => toPuzzleConfig(state, { defaultTitle }), [state, defaultTitle]);

  useEffect(() => {
    track("creator_started", { resumed: !!initialConfig });
    // One draft per user: if one already exists for another puzzle, leave it alone.
    let cancelled = false;
    if (!initialConfig) {
      fetchDraft()
        .then((existing) => {
          if (cancelled) return;
          if (existing && existing.config && existing.config._puzzleId !== state.puzzleId) {
            autosaveRef.current = false;
            logger.info("creator v2: autosave off, another draft exists");
          }
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async () => {
    if (!autosaveRef.current || !isDraftable(state)) return;
    const config = toDraftConfig(state, { defaultTitle });
    const fingerprint = JSON.stringify(config);
    if (fingerprint === lastSavedRef.current) return;
    lastSavedRef.current = fingerprint;
    try {
      await saveDraft(config, "full");
    } catch (err) {
      logger.warn("creator v2: draft autosave failed", { error: err && err.message });
    }
  }, [state, defaultTitle]);

  // Save when the screen changes — a handful of writes per puzzle, not one per keystroke.
  useEffect(() => {
    track("step_viewed", { step: state.step });
    persist();
  }, [state.step]);

  const uploadMedia = useCallback(
    (file, kind) => uploadPuzzleMedia({ puzzleId: state.puzzleId, file, kind }),
    [state.puzzleId]
  );

  // The app sends the user to My Puzzles when a draft was saved (so they see
  // it there), and back where they came from when there was nothing to save.
  const exit = useCallback(async () => {
    if (!autosaveRef.current && isDraftable(state)) {
      setExitAsk(true);
      return;
    }
    const draftSaved = autosaveRef.current && isDraftable(state);
    await persist();
    onExit({ draftSaved });
  }, [persist, onExit, state]);

  // "Save it": this puzzle becomes the draft, replacing the older one.
  const saveAndExit = useCallback(async () => {
    autosaveRef.current = true;
    await persist();
    setExitAsk(false);
    onExit({ draftSaved: true });
  }, [persist, onExit]);

  const discardAndExit = useCallback(() => {
    setExitAsk(false);
    onExit({ draftSaved: false });
  }, [onExit]);

  const atLimitDoor = !canCreate && !limitAcknowledged;

  // One system back press = one step back, the same as the on-screen arrow.
  // Only the first screen (or the limit screen) leaves, and it leaves through
  // `exit`, so a draft is saved or asked about rather than lost.
  const systemBack = () => {
    if (exitAsk) return setExitAsk(false);
    if (atLimitDoor) return onExit({ draftSaved: false });
    if (playthrough) return setPlaythrough(null);
    if (state.step === "share") return dispatch({ type: "go", step: "ready" });
    if (state.step === "name") return exit();
    return dispatch({ type: "back" });
  };
  const systemBackRef = useRef(systemBack);
  systemBackRef.current = systemBack;
  useEffect(() => {
    if (!backRef) return undefined;
    backRef.current = () => systemBackRef.current();
    return () => { backRef.current = null; };
  }, [backRef]);

  // ── the limit, said at the door ──
  if (atLimitDoor) {
    return (
      <LimitScreen
        puzzles={puzzles}
        limit={slotLimit}
        onDelete={onDeletePuzzle}
        onUpgrade={onUpgrade}
        onBuildAnyway={() => setLimitAcknowledged(true)}
        onClose={onExit}
      />
    );
  }

  // ── try it before sending: the player, over the unpublished puzzle ──
  if (playthrough) {
    return (
      <div style={{ position: "relative", minHeight: "100dvh", ...themeVarsFor(state.theme), ...paletteVars(state.theme), background: "var(--color-bg)" }}>
        <PreviewBar name={state.recipientName} onBack={() => setPlaythrough(null)} dark={isDarkPalette(state.theme)} />
        <div style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${PREVIEW_BAR_HEIGHT}px)` }}>
          {playthrough === "play" ? (
            <LevelPlay config={puzzleConfig} onFinale={() => setPlaythrough("finale")} />
          ) : (
            <Finale config={puzzleConfig} onCreateOwn={() => setPlaythrough(null)} />
          )}
        </div>
      </div>
    );
  }

  // ── publishing: the existing share screen does the write and shows the link ──
  if (state.step === "share") {
    return (
      <ShareScreen
        config={puzzleConfig}
        onBack={() => dispatch({ type: "go", step: "ready" })}
        onNewPuzzle={onNewPuzzle || (() => onExit({ draftSaved: false }))}
        onPrivacy={onPrivacy}
        onInviteSignIn={onInviteSignIn}
        onShared={onShared}
        onPublished={(id, url, cfg) => {
          track("publish_succeeded", {
            clues: completeClues(state).length,
            sealed: !!state.seal.enabled,
            theme: state.theme,
            finale: state.finale ? state.finale.type : "none",
          });
          onPublished(id, url, cfg);
        }}
      />
    );
  }

  const clue = currentClue(state);
  const previewLevels = puzzleConfig.levels;
  const previewIndex = Math.max(0, state.clues.filter(isClueComplete).indexOf(clue));

  const screens = {
    name: <NameScreen state={state} dispatch={dispatch} />,
    vibe: <VibeScreen state={state} dispatch={dispatch} themes={themes} isPro={isPro} onLocked={onLocked} now={now} allSeasons={allSeasons} />,
    question: <QuestionScreen state={state} dispatch={dispatch} />,
    answer: <AnswerScreen state={state} dispatch={dispatch} />,
    celebration: <CelebrationScreen state={state} dispatch={dispatch} isPro={isPro} onMediaLocked={onMediaLocked} />,
    celebrationDetail: <CelebrationDetailScreen state={state} dispatch={dispatch} onUploadMedia={uploadMedia} />,
    reward: (
      <RewardScreen
        state={state}
        dispatch={dispatch}
        LevelPlay={LevelPlay}
        themeVars={themeVarsFor(state.theme)}
        previewConfig={{ ...puzzleConfig, levels: previewLevels.length ? previewLevels : [] }}
        previewIndex={previewIndex}
      />
    ),
    hint: <HintScreen state={state} dispatch={dispatch} />,
    clueName: <ClueNameScreen state={state} dispatch={dispatch} />,
    list: <ClueListScreen state={state} dispatch={dispatch} />,
    reveal: <RevealScreen state={state} dispatch={dispatch} isPro={isPro} onMediaLocked={onMediaLocked} onUploadMedia={uploadMedia} />,
    seal: <SealScreen state={state} dispatch={dispatch} />,
    word: <WordScreen state={state} dispatch={dispatch} />,
    ready: (
      <ReadyScreen
        state={state}
        dispatch={dispatch}
        defaultTitle={defaultTitle}
        onPlayThrough={() => setPlaythrough("play")}
        slotsNote={canCreate ? tr("app.creator.ready.slotsNote") : tr("app.creator.ready.atLimitNote")}
      />
    ),
  };

  const next = (intent) => dispatch({ type: "next", intent });

  function footerFor(step) {
    switch (step) {
      case "name":
      case "vibe":
        return startFooter({ state, tr, step, onNext: () => next() });
      case "reward":
        return rewardFooter({ state, tr, dispatch });
      case "list":
        return listFooter({ state, tr, dispatch });
      case "ready":
        return canCreate
          ? readyFooter({ tr, onPublish: () => next(), publishing })
          : (
            <PrimaryButton
              testId="creator-save-draft"
              icon={null}
              disabled={publishing}
              onClick={async () => {
                setPublishing(true);
                await onSaveDraftExplicit(toDraftConfig(state, { defaultTitle }), "full");
                setPublishing(false);
              }}
            >
              {tr("app.creator.ready.saveDraft")}
            </PrimaryButton>
          );
      case "celebration":
        return (
          <PrimaryButton testId="creator-next" onClick={() => next()}>
            {(clue && clue.celebrationType) === "default" ? tr("app.creator.celebration.ctaDefault") : tr("app.creator.common.next")}
          </PrimaryButton>
        );
      case "answer":
        return (
          <PrimaryButton testId="creator-next" onClick={() => next()} disabled={!canContinue(state)}>
            {tr("app.creator.answer.cta")}
          </PrimaryButton>
        );
      case "hint":
      case "clueName":
        return (
          <PrimaryButton testId="creator-next" onClick={() => next()} icon={null}>
            {tr("app.creator.common.done")}
          </PrimaryButton>
        );
      case "reveal":
        return (
          <PrimaryButton testId="creator-next" onClick={() => next()} disabled={!canContinue(state)}>
            {tr("app.creator.common.next")}
          </PrimaryButton>
        );
      case "seal":
        return (
          <PrimaryButton testId="creator-next" onClick={() => next()}>
            {state.seal.enabled ? tr("app.creator.seal.ctaYes") : tr("app.creator.seal.ctaNo")}
          </PrimaryButton>
        );
      default:
        return (
          <PrimaryButton testId="creator-next" onClick={() => next()} disabled={!canContinue(state)}>
            {tr("app.creator.common.next")}
          </PrimaryButton>
        );
    }
  }

  return (
    <CreatorShell
      theme={state.theme}
      segment={segmentOf(state.step)}
      onBack={state.step === "name" ? undefined : () => dispatch({ type: "back" })}
      onClose={exit}
      footer={footerFor(state.step)}
      overlay={exitAsk ? (
        <ExitSheet
          name={state.recipientName}
          onSave={saveAndExit}
          onDiscard={discardAndExit}
          onStay={() => setExitAsk(false)}
        />
      ) : null}
    >
      {screens[state.step] || screens.name}
      {/* Keeps the last field clear of the fixed footer on short screens. */}
      <div style={{ height: 8 }} aria-hidden="true" />
    </CreatorShell>
  );
}
